/** Daily cycle balance snapshots (ops pipeline → public/data/cycle-snapshots.json). */

export type CycleSnapshot = {
	timestamp: number;
	balances: Record<string, string>;
};

export type CycleSnapshotFile = {
	updatedAt?: number;
	network?: string;
	snapshots?: CycleSnapshot[];
};

const SNAPSHOTS_URL = "./data/cycle-snapshots.json";

export async function loadCycleSnapshotFile(): Promise<CycleSnapshotFile | null> {
	try {
		const response = await fetch(SNAPSHOTS_URL, { cache: "no-cache" });
		if (!response.ok) return null;
		return (await response.json()) as CycleSnapshotFile;
	} catch {
		return null;
	}
}

const TRILLION = 1_000_000_000_000n;
const BILLION = 1_000_000_000n;

export function formatCycles(value: bigint): string {
	if (value >= TRILLION) {
		const whole = value / TRILLION;
		const frac = (value % TRILLION) / (TRILLION / 100n);
		return frac > 0n
			? `${whole}.${frac.toString().padStart(2, "0")}T`
			: `${whole}T`;
	}
	if (value >= BILLION) {
		const whole = value / BILLION;
		const frac = (value % BILLION) / (BILLION / 10n);
		return frac > 0n ? `${whole}.${frac}B` : `${whole}B`;
	}
	return value.toLocaleString();
}

export function shortCanisterId(id: string): string {
	if (id.length < 12) return id;
	return `${id.slice(0, 5)}…${id.slice(-5)}`;
}

/** Known env keys → friendly labels for snapshot rows. */
const LABEL_BY_ENV: Array<{ keys: string[]; label: string }> = [
	{ keys: ["VITE_PROTOCOL_CANISTER_ID", "VITE_CANISTER_ID_PROTOCOL"], label: "Protocol" },
	{ keys: ["VITE_CANISTER_ID_ORACLE"], label: "Oracle" },
	{ keys: ["VITE_CANISTER_ID_USDM_LEDGER"], label: "USDM ledger" },
	{ keys: ["VITE_CANISTER_ID_MMK_LEDGER"], label: "MMK ledger" },
	{ keys: ["VITE_CANISTER_ID_ASSETS"], label: "Assets SPA" },
	{ keys: ["VITE_CANISTER_ID_AGENT_GATEWAY", "VITE_AGENT_GATEWAY_CANISTER_ID"], label: "Agent gateway" },
	{ keys: ["VITE_CANISTER_ID_PROTOCOL_ASSISTANT", "VITE_PROTOCOL_ASSISTANT_CANISTER_ID"], label: "Assistant" },
	{ keys: ["VITE_CANISTER_ID_DEMO_FAUCET"], label: "Demo faucet" },
	{ keys: ["VITE_CANISTER_ID_TREASURY_INDEX"], label: "Treasury index" },
];

export function labelForCanisterId(id: string): string {
	const env = import.meta.env as Record<string, string | undefined>;
	for (const row of LABEL_BY_ENV) {
		for (const k of row.keys) {
			if (env[k] === id) return row.label;
		}
	}
	return shortCanisterId(id);
}

export type CycleReserveRow = {
	canisterId: string;
	label: string;
	balance: bigint;
	/** prev − current (positive = spent); null if no prior point */
	spend: bigint | null;
};

export function buildReserveRows(file: CycleSnapshotFile | null): {
	network: string;
	updatedAt: number | null;
	points: number;
	rows: CycleReserveRow[];
	totalBalance: bigint;
	totalSpend: bigint | null;
} {
	const snapshots = [...(file?.snapshots ?? [])].sort(
		(a, b) => a.timestamp - b.timestamp,
	);
	const last = snapshots[snapshots.length - 1];
	const prev = snapshots.length >= 2 ? snapshots[snapshots.length - 2] : null;
	if (!last) {
		return {
			network: file?.network ?? "—",
			updatedAt: file?.updatedAt ?? null,
			points: 0,
			rows: [],
			totalBalance: 0n,
			totalSpend: null,
		};
	}

	const rows: CycleReserveRow[] = Object.entries(last.balances)
		.map(([canisterId, bal]) => {
			const balance = BigInt(bal);
			let spend: bigint | null = null;
			if (prev?.balances[canisterId] != null) {
				try {
					spend = BigInt(prev.balances[canisterId]) - balance;
				} catch {
					spend = null;
				}
			}
			return {
				canisterId,
				label: labelForCanisterId(canisterId),
				balance,
				spend,
			};
		})
		.sort((a, b) => (a.balance < b.balance ? 1 : a.balance > b.balance ? -1 : 0));

	const totalBalance = rows.reduce((s, r) => s + r.balance, 0n);
	const spends = rows.map((r) => r.spend).filter((s): s is bigint => s != null);
	const totalSpend =
		spends.length > 0 ? spends.reduce((s, x) => s + x, 0n) : null;

	return {
		network: file?.network ?? "—",
		updatedAt: file?.updatedAt ?? last.timestamp,
		points: snapshots.length,
		rows,
		totalBalance,
		totalSpend,
	};
}
